// WlpAdoptionPanel — B3.3 Light, Feb 2026.
//
// Admin-only mini-dashboard rendered inside /admin/marketplace/demand.
// Reads /api/wlp-adoption/admin/metrics (k-anon enforced server-side,
// no PII surfaced) and shows the WLP adoption funnel + the Phase 2
// readiness gauge.
//
// Strict privacy invariants:
//   • No user_id, email, or name is ever rendered.
//   • The server returns nulls when total_eligible < k-anon floor (5);
//     we render "—" placeholders + the "Insufficient signal" state.
//   • Read-only — no mutating actions in this panel.

import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Users, Heart, TrendingUp, Eye, MousePointerClick, X, CheckCircle2,
  Target, RefreshCw, ShieldCheck, AlertTriangle,
} from "lucide-react";

const PHASE2_TARGET = 25;
const APPROACHING_RATIO = 0.6; // ≥ 60% of target → "Approaching"

const fmt = (v) => (v === null || v === undefined ? "—" : String(v));
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

const MetricCard = ({ icon: Icon, label, value, hint, testid }) => (
  <div
    className="rounded-md border border-slate-200 bg-white p-3 flex items-start gap-3"
    data-testid={testid}
  >
    <Icon className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
    <div className="min-w-0 flex-1">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-900 mt-0.5 tabular-nums" data-testid={`${testid}-value`}>
        {value}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{hint}</div>}
    </div>
  </div>
);

const StatusBanner = ({ kind, title, body }) => {
  const map = {
    insufficient: {
      icon: AlertTriangle,
      bg: "bg-amber-50",
      border: "border-amber-200",
      iconCls: "text-amber-600",
      titleCls: "text-amber-900",
      bodyCls: "text-amber-800",
    },
    approaching: {
      icon: TrendingUp,
      bg: "bg-sky-50",
      border: "border-sky-200",
      iconCls: "text-sky-600",
      titleCls: "text-sky-900",
      bodyCls: "text-sky-800",
    },
    ready: {
      icon: CheckCircle2,
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      iconCls: "text-emerald-600",
      titleCls: "text-emerald-900",
      bodyCls: "text-emerald-800",
    },
  }[kind];
  const Icon = map.icon;
  return (
    <div
      className={`rounded-md border ${map.border} ${map.bg} p-3 flex items-start gap-3`}
      data-testid={`wlp-adoption-status-${kind}`}
    >
      <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${map.iconCls}`} />
      <div className="min-w-0">
        <div className={`text-sm font-semibold ${map.titleCls}`}>{title}</div>
        <div className={`text-xs mt-0.5 leading-relaxed ${map.bodyCls}`}>{body}</div>
      </div>
    </div>
  );
};

export const WlpAdoptionPanel = ({ t }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/wlp-adoption/admin/metrics");
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.status === 404 ? "disabled" : "error");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Module disabled → render nothing (matches WLP eligibility hook pattern).
  if (err === "disabled") return null;

  const useful = data?.total_with_useful_wlp ?? null;
  const eligible = data?.total_eligible ?? null;
  const adoption = data?.adoption_rate_pct ?? null;
  const collapsed = data?.collapsed_reason === "below_k_anon_floor";

  // Derive panel status (k-anon collapsed counts as Insufficient).
  let statusKind;
  if (collapsed || useful === null || useful === 0) statusKind = "insufficient";
  else if (useful >= PHASE2_TARGET) statusKind = "ready";
  else if (useful >= Math.ceil(APPROACHING_RATIO * PHASE2_TARGET)) statusKind = "approaching";
  else statusKind = "insufficient";

  const progressPct = useful !== null
    ? Math.min(100, Math.round((useful / PHASE2_TARGET) * 100))
    : 0;

  const statusTitle = t(`marketplace.demand.adoptionStatus${
    statusKind.charAt(0).toUpperCase() + statusKind.slice(1)
  }`);
  const statusBody = t(`marketplace.demand.adoptionStatus${
    statusKind.charAt(0).toUpperCase() + statusKind.slice(1)
  }Body`);

  return (
    <div
      className="rounded-xl border border-emerald-100 bg-white p-5 sm:p-6 space-y-4"
      data-testid="wlp-adoption-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex items-start gap-3">
          <Heart className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              {t("marketplace.demand.adoptionTitle")}
            </h3>
            <p className="text-xs text-slate-600 mt-0.5 max-w-2xl leading-snug">
              {t("marketplace.demand.adoptionSub")}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="wlp-adoption-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 me-1.5 ${loading ? "animate-spin" : ""}`} />
          {t("marketplace.demand.adoptionRefresh")}
        </Button>
      </div>

      {err === "error" && (
        <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800" data-testid="wlp-adoption-error">
          {t("marketplace.demand.adoptionLoadError")}
        </div>
      )}

      {/* Metric grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetricCard
          icon={Users}
          label={t("marketplace.demand.adoptionMetricEligible")}
          value={fmt(eligible)}
          hint={t("marketplace.demand.adoptionMetricEligibleHint")}
          testid="wlp-adoption-eligible"
        />
        <MetricCard
          icon={Heart}
          label={t("marketplace.demand.adoptionMetricWithWlp")}
          value={fmt(useful)}
          hint={t("marketplace.demand.adoptionMetricWithWlpHint")}
          testid="wlp-adoption-with-wlp"
        />
        <MetricCard
          icon={TrendingUp}
          label={t("marketplace.demand.adoptionMetricRate")}
          value={fmtPct(adoption)}
          testid="wlp-adoption-rate"
        />
        <MetricCard
          icon={CheckCircle2}
          label={t("marketplace.demand.adoptionMetricCompleted")}
          value={fmt(data?.total_completed)}
          testid="wlp-adoption-completed"
        />
        <MetricCard
          icon={Eye}
          label={t("marketplace.demand.adoptionMetricImpressions")}
          value={fmt(data?.total_impressions)}
          testid="wlp-adoption-impressions"
        />
        <MetricCard
          icon={MousePointerClick}
          label={t("marketplace.demand.adoptionMetricClicks")}
          value={fmt(data?.total_clicks)}
          testid="wlp-adoption-clicks"
        />
        <MetricCard
          icon={X}
          label={t("marketplace.demand.adoptionMetricDismissed")}
          value={fmt(data?.total_dismissed)}
          testid="wlp-adoption-dismissed"
        />
        <MetricCard
          icon={Target}
          label={t("marketplace.demand.milestoneTarget")}
          value={String(PHASE2_TARGET)}
          testid="wlp-adoption-target"
        />
      </div>

      {/* Progress bar */}
      <div className="rounded-md border border-emerald-100 bg-emerald-50/40 p-3" data-testid="wlp-adoption-progress">
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-900">
            {t("marketplace.demand.adoptionProgressLabel")}
          </span>
          <span className="text-[11px] font-mono text-slate-600 tabular-nums">
            <span data-testid="wlp-adoption-progress-current">{fmt(useful)}</span>
            <span className="text-slate-300"> / </span>
            <span data-testid="wlp-adoption-progress-target">{PHASE2_TARGET}</span>
            <span className="text-slate-400"> · {progressPct}%</span>
          </span>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-white border border-emerald-100 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
            style={{ width: `${progressPct}%` }}
            data-testid="wlp-adoption-progress-bar"
            aria-label={`${useful ?? 0} / ${PHASE2_TARGET}`}
          />
        </div>
      </div>

      {/* Status banner */}
      <StatusBanner kind={statusKind} title={statusTitle} body={statusBody} />

      {/* K-anon note footer */}
      <div className="flex items-center gap-2 text-[11px] text-slate-500" data-testid="wlp-adoption-kanon-note">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
        <span>{t("marketplace.demand.adoptionKAnonNote")}</span>
      </div>
    </div>
  );
};

export default WlpAdoptionPanel;
